/* eslint-disable */
import { modalActions } from '@/Actions';
import drawerActions from '@/Actions/drawer.actions';
import ImagesFinder from '@/Components/ImagesFinder/ImagesFinder';
import { AddGroupModal } from '@/Components/Modals/AddGroupModal';
import ProductSchedulingTimeModal from '@/Components/Modals/ProductSchedulingTimeModal';
import TabsWithButtons from '@/Components/TabswithButtons/TabsWithButtons';
import { Calendar, CalendarDays, Filter, Loader, Plus, UsersRound, Check, X, Pencil, Eye, Store, Package, Save } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import GroupCreationCard from './GroupCreationCard';
import GroupView from './GroupView';
import { notificationServices, productServices } from '@/Services';
import { useProductData } from '@/Helpers';
import { useAuth } from '@/Components/Auth/Login/auth';
import { Button } from '@/Components/ui/button';


const tabList = [
    {
        value: 'addSchedule',
        icon: <UsersRound size={20} />,
        label: 'Add Schedule',
    },
    {
        value: 'viewSchedule',
        icon: <Calendar size={20} />,
        label: 'View Schedule',
    },
];
const exportInfo = {
    hideExportAccordian: true,
};

const filtersInfo = {
    header: {
        store: false,
    },
    store: {
        show: true,
        text: 'Select Store',
        multiSelect: true,
    },
}

const initialGroupCard = {
    id: null,
    groupName: '',
    dates: [],
    stores: [],
    containerList: {},
};

const getTotalDaysMonth = () => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
};

function ProductScheduling() {
    const dispatch = useDispatch();
    const { userConfig = {} } = useAuth()
    const { level } = userConfig?.userDetails || {};
    const originalProductList = useSelector((state) => state.form.ALL_PRODUCTS.data.LIST);
    const [activeTab, setActiveTab] = useState(tabList[0].value);
    const [filters, setFilters] = useState({});
    const [groupCards, setGroupCards] = useState([]);
    const [viewScheduleData, setViewScheduleData] = useState([]);
    const [loading, setLoading] = useState(false);
    const groupId = useRef(1);
    const getEditedDataRef = useRef(null);
    // Edit mode state
    const [isEditMode, setIsEditMode] = useState(false);
    const productList = useMemo(() => {
        return originalProductList?.filter((item) => item?.skuType === 'PRODUCT')
    }, [originalProductList]);

    const openGroupModal = useCallback(() => {
        dispatch(modalActions.toggleAddGroupModal(true));
    }, [dispatch]);

    const openFilterDrawer = useCallback(() => {
        dispatch(drawerActions.toggleFiltersDrawer(true));
    }, [dispatch]);

    const handleOpenProductSchedulingModal = useCallback(() => {
        dispatch(modalActions.toggleProductScheduleTimeModal(true, {
            onConfirm: (req) => handleSaveGroup(req)
        }));
    }, [dispatch, groupCards]);

    const fetchGroupList = useCallback(async (filters) => {
        setLoading(true);
        const req = {
            data: {
                storeIds: (!filters?.multiStore?.id || filters?.multiStore?.id.includes(-999)) ? [] : filters?.multiStore?.id,
            }
        }
        try {
            const response = await productServices?.getProductGroupList(req);
            if (response?.statusCode === 200) {
                setViewScheduleData(response?.data)
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleEnterEditMode = useCallback(() => {
        const initialData = {};
        viewScheduleData.forEach((group) => {
            initialData[group.id] = {
                stores: group.storeIds || [],
                days: (group.configValues || []).reduce((acc, day) => {
                    acc[day] = true;
                    return acc;
                }, {}),
                productIds: group.productIds || [],
            };
        });
        setIsEditMode(true);
    }, [viewScheduleData]);

    // Handle exiting edit mode without saving
    const handleExitEditMode = useCallback(() => {
        setIsEditMode(false);
    }, []);


    // Handle saving all changes - uses data from GroupView via ref
    const handleSaveAllChanges = useCallback(async () => {
        if (!getEditedDataRef.current) return;

        const editedData = getEditedDataRef.current();
        if (!editedData || editedData.length === 0) return;
        const groupsWithoutProducts = editedData.filter(
            (group) => !group.productIds || group.productIds.length === 0
        );
        if (groupsWithoutProducts.length > 0) {
            notificationServices?.generateNotification({
                type: 'error',
                message: `Please add products to: ${groupsWithoutProducts.map(g => g.groupName).join(', ')}`,
            });
            return;
        }

        const groupsWithoutStores = editedData.filter(
            (group) => !group.storeIds || group.storeIds.length === 0
        );
        if (groupsWithoutStores.length > 0) {
            notificationServices?.generateNotification({
                type: 'error',
                message: `Please select stores for: ${groupsWithoutStores.map(g => g.groupName).join(', ')}`,
            });
            return;
        }

        setLoading(true);
        const notificationId = notificationServices?.generateNotification({
            type: 'loading',
            message: 'Saving changes...',
        });

        try {
            // Save all groups in parallel using Promise.all
            await Promise.all(
                editedData.map((groupData) => {
                    const req = {
                        data: {
                            groupId: groupData.groupId,
                            groupName: groupData.groupName,
                            productIds: groupData.productIds,
                            configType: groupData.configType,
                            configValues: groupData.configValues,
                            storeIds: groupData.storeIds,
                        }
                    };
                    return productServices.editProductGroup(req);
                })
            );

            notificationServices?.generateNotification({
                type: 'success',
                message: 'All changes saved successfully',
            });

            // Refresh the data and exit edit mode
            await fetchGroupList(filters);
            setIsEditMode(false);
        } catch (error) {
            console.log(error);
            notificationServices?.generateNotification({
                type: 'error',
                message: error ?? 'Failed to save changes',
            });
        } finally {
            setLoading(false);
            notificationServices?.closeNotification(notificationId);
        }
    }, [filters, fetchGroupList]);

    // Callback to receive getEditedData function from GroupView
    const handleSetGetEditedData = useCallback((getEditedDataFn) => {
        getEditedDataRef.current = getEditedDataFn;
    }, []);

    const buttonList = useMemo(() => [
        ...(activeTab === 'addSchedule' && level !== 1
            ? [
                {
                    label: 'Add Group',
                    icon: <Plus size={20} />,
                    onClick: openGroupModal,
                    cssTailwind: 'bg-yellow-500',
                },
                {
                    label: 'Add Schedule',
                    icon: <CalendarDays size={20} />,
                    onClick: handleOpenProductSchedulingModal,
                    cssTailwind: 'bg-blue-200 hover:bg-blue-300 text-black font-medium px-4 py-2',
                }
            ]
            : []),
        ...(activeTab === 'viewSchedule'
            ? [
                ...(isEditMode
                    ? [
                        {
                            label: 'Save',
                            icon: <Save size={20} />,
                            onClick: handleSaveAllChanges,
                            cssTailwind: 'bg-yellow-500',
                        },
                        {
                            label: 'Cancel',
                            icon: <X size={20} />,
                            onClick: handleExitEditMode,
                            cssTailwind: 'bg-slate-200 hover:bg-slate-300 text-black font-medium px-4 py-2',
                        }
                    ]
                    : level !== 1
                        ? [
                            {
                                label: 'Edit',
                                icon: <Pencil size={20} />,
                                onClick: handleEnterEditMode,
                                cssTailwind: 'bg-blue-200 hover:bg-blue-300 text-black font-medium px-4 py-2',
                            }
                        ]
                        : []),

                {
                    label: 'Filter',
                    icon: <Filter size={20} />,
                    onClick: openFilterDrawer,
                    cssTailwind: 'bg-teal-600 text-white',
                }
            ]
            : [])
    ], [
        activeTab,
        level,
        isEditMode,
        openGroupModal,
        handleOpenProductSchedulingModal,
        handleSaveAllChanges,
        handleExitEditMode,
        handleEnterEditMode,
        openFilterDrawer,
    ]);

    const onFilterChange = useCallback((filterValue) => {
        setFilters(filterValue);
        if (activeTab === 'viewSchedule') {
            fetchGroupList(filterValue);
        }
    }, [activeTab, fetchGroupList]);

    const handleTabChange = useCallback((tabValue) => {
        setActiveTab(tabValue);
        if (tabValue === 'viewSchedule') {
            fetchGroupList(filters);
        }
    }, [filters, fetchGroupList]);

    const totalDays = getTotalDaysMonth();
    const days = useMemo(() => Array.from({ length: totalDays }, (_, i) => i + 1), [totalDays]);

    const viewScheduleTableData = useMemo(() => {
        return viewScheduleData?.map((group, index) => ({
            ...group,
            productCount: group?.productIds?.length || 0,
            sno: index + 1,
        })) || [];
    }, [viewScheduleData]);

    const handleAddGroup = useCallback((groupName) => {
        const newGroup = {
            ...initialGroupCard,
            id: groupId?.current,
            containerList: {
                firstContainerList: productList,
                secondContainerList: [],
            },
            groupName,
        }
        setGroupCards((prev) => {
            return [...prev, newGroup];
        });
        groupId.current++;
    }, [groupId, productList]);

    const toggleModal = useCallback(() => {
        dispatch(modalActions.toggleAddGroupModal(false));
    }, [dispatch]);

    const getContainerList = useCallback((list, id) => {
        setGroupCards((prev) => {
            return prev.map((item) => {
                if (item?.id === id) {
                    return {
                        ...item,
                        containerList: list,
                    }
                }
                return item;
            })
        })
    }, []);

    const handleSaveGroup = useCallback(async (reqData) => {
        if (!reqData || reqData.length === 0) return;

        setLoading(true);
        const notificationId = notificationServices?.generateNotification({
            type: 'loading',
            message: 'Creating groups...',
        });

        try {
            // Create all groups in parallel using Promise.all
            await Promise.all(
                reqData.map((group) => {
                    const req = {
                        data: {
                            ...group,
                        },
                    };
                    return productServices.createProductGroup(req);
                })
            );

            notificationServices?.generateNotification({
                type: 'success',
                message: 'Groups created successfully',
            });
            setGroupCards([]);
        } catch (error) {
            console.log(error);
            notificationServices?.generateNotification({
                type: 'error',
                message: error ?? 'Failed to create groups',
            });
            throw error;
        } finally {
            setLoading(false);
            notificationServices?.closeNotification(notificationId);
        }
    }, []);


    const handleDelete = useCallback((id) => {
        setGroupCards((prev) => prev.filter((val) => val?.id !== id));
    }, []);

    return (
        <div className='flex flex-col gap-2 p-4'>
            <AddGroupModal toggleModal={toggleModal} onSubmit={handleAddGroup} />
            <ImagesFinder
                filtersInfo={filtersInfo}
                onSubmit={onFilterChange}
                exportInfo={exportInfo}
                avoidInitialCall={true}
            />
            <ProductSchedulingTimeModal groupList={groupCards} setGroupList={setGroupCards} />
            <p className='font-semibold'>Product Scheduling</p>
            <span>Add products and schedule their preparation times</span>
            {productList?.length === 0 ? (
                <div className='flex justify-center items-center h-full'>
                    <Button
                        onClick={() => useProductData(dispatch)}
                        className='bg-blue-500 hover:bg-blue-600 flex justify-center items-center text-white  font-medium px-4 py-2 rounded-md transition'
                        disabled={loading}
                    >
                        Fetch Products
                    </Button>
                </div>
            ) : (
                <div className='bg-[#F9FAFB] p-4 mt-4 rounded-md h-[calc(100vh-200px)] flex flex-col'>
                    <TabsWithButtons
                        tabList={tabList}
                        buttonList={buttonList}
                        initialTab={activeTab}
                        onTabChange={(tabValue) => handleTabChange(tabValue)}
                        filters={filters}
                        filtersInfo={filtersInfo}
                    />
                    {/* group Creation Card */}
                    {groupCards?.length > 0 && activeTab === 'addSchedule' && (
                        <div className='flex flex-col gap-3 overflow-y-auto flex-1 pr-2'>
                            {groupCards?.map((item) => {
                                return (
                                    <GroupCreationCard
                                        item={item}
                                        handleDelete={handleDelete}
                                        getContainerList={getContainerList}
                                    />
                                )
                            })}
                        </div>
                    )}
                    {groupCards?.length === 0 && activeTab === 'addSchedule' && (
                        <div className='mt-2'>
                            Please create a group to continue.
                        </div>
                    )}
                    {viewScheduleData?.length === 0 && activeTab === 'viewSchedule' && (
                        <div className='mt-2'>
                            Please create groups to View Schedule.
                        </div>
                    )}
                    {activeTab === 'viewSchedule' && viewScheduleData?.length > 0 && (
                        <div className='flex-1 overflow-hidden bg-white rounded-lg border border-gray-200'>
                            {loading ? (
                                <div className='flex justify-center items-center gap-2 text-center text-yellow-600 h-full'>
                                    <Loader size={20} /> loading list...
                                </div>
                            ) : (
                                <GroupView
                                    data={viewScheduleTableData}
                                    productList={productList}
                                    days={days}
                                    isEditMode={isEditMode}
                                    onSave={handleSetGetEditedData}
                                />
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
export default ProductScheduling;